import type { ReactNode } from 'react';
import type { AggregatedMetrics, CurrencyStats } from './utils';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { formatMinorUnits } from 'helpers/formatters';
import { CurrencyCard } from './CurrencyCard';

export type ExpandedContentProps = {
  isExpanded: boolean;
  aggregated: AggregatedMetrics;
  statsByCurrency: Record<string, CurrencyStats>;
  updatedAt: Date | null;
};

export const ExpandedContent = (props: ExpandedContentProps) => {
  const { isExpanded, aggregated, statsByCurrency, updatedAt } = props;

  const formattedRevenueDelta = formatMinorUnits(aggregated.totalRevenue * 0.15, aggregated.primaryCurrency);

  const currencyEntries = Object.entries(statsByCurrency);

  return (
    <Collapsible open={isExpanded}>
      <CollapsibleContent>
        <div className="bg-panel2 border-line flex flex-col gap-3 border-t p-3.5">
          <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
            <Summary label="Conversion Rate">{aggregated.conversionRate.toFixed(2)}%</Summary>
            <Summary label="Revenue Delta">+{formattedRevenueDelta} vs. control</Summary>
            <Summary label="Last Updated">{updatedAt ? new Date(updatedAt).toLocaleString() : 'N/A'}</Summary>
          </div>

          {currencyEntries.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="micro-label">Detailed Metrics by Currency</span>
              <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
                {currencyEntries.map(([currency, data]) => (
                  <CurrencyCard key={currency} currency={currency} data={data} />
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

type SummaryProps = { label: string; children: ReactNode };

const Summary = ({ label, children }: SummaryProps) => (
  <div className="border-line grid content-start gap-[7px] rounded-card border p-[13px]">
    <span className="micro-label">{label}</span>
    <span className="num">{children}</span>
  </div>
);
